import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import {
  Loader2,
  Tag,
  Percent,
  Truck,
  Gift,
  Package,
  Store,
  ChevronDown,
  ChevronUp,
  Check,
  Search,
  X,
} from 'lucide-react';

import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Textarea } from '@/components/atoms/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import { Switch } from '@/components/atoms/switch';
import { Badge } from '@/components/atoms/badge';
import { Checkbox } from '@/components/atoms/checkbox';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/molecules/form-field';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/atoms/dialog';
import { FormPageHeader, FormProgressIndicator } from '@/components/forms';
import { PriceConverter } from '@/components/molecules/price-converter';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';

import { useCreatePromotion } from '@/features/promotions/hooks/use-create-promotion';
import { useShops } from '@/features/shops/hooks/use-shops';
import { useProducts } from '@/features/products/hooks/use-products';
import {
  createPromotionSchema,
  PROMOTION_TYPES,
  type CreatePromotionFormData,
} from '@/features/promotions/schemas/create-promotion.schema';
import type { PromotionType } from '@/features/promotions/types/promotions.types';
import { type CurrencyType, convertPrice } from '@/lib/utils/price.utils';
import { useExchangeRates } from '@/lib/hooks/use-exchange-rates';
import { getImageUrl, formatPrice } from '@/lib/utils';
import { CURRENCIES } from '@/lib/constants/form-options';

const typeIconMap: Record<PromotionType, React.ElementType> = {
  percentage: Percent,
  fixed_amount: Tag,
  free_shipping: Truck,
  buy_x_get_y: Gift,
  bundle: Package,
};

const BADGE_COLORS = [
  '#FF5722', '#E91E63', '#9C27B0', '#673AB7',
  '#3F51B5', '#2196F3', '#00BCD4', '#009688',
  '#4CAF50', '#8BC34A', '#CDDC39', '#FFC107',
  '#FF9800', '#795548', '#607D8B', '#000000',
];

type SelectionMode = 'all' | 'shops' | 'products';

export default function NewPromotionPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Selection mode state (same as mobile-pro)
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('all');
  const [selectedShopIds, setSelectedShopIds] = useState<string[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [activateNow, setActivateNow] = useState(false);
  const [valueCurrency, setValueCurrency] = useState<CurrencyType>('european_currency');

  // Modals
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [showShopSelector, setShowShopSelector] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  // Collapsible sections
  const [limitsOpen, setLimitsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);

  // Data
  const { data: shops = [] } = useShops();
  const { data: productsData, isLoading: isLoadingProducts } = useProducts({ search: productSearch });
  const products = productsData?.products ?? [];
  const { mutate: createPromotion, isPending: isSubmitting } = useCreatePromotion();
  const { exchangeRates } = useExchangeRates();

  const form = useForm<CreatePromotionFormData>({
    resolver: zodResolver(createPromotionSchema),
    defaultValues: {
      name: '',
      displayName: '',
      description: '',
      type: 'percentage',
      value: 0,
      buyQuantity: undefined,
      getQuantity: undefined,
      minPurchaseAmount: undefined,
      minQuantity: undefined,
      maxUsage: undefined,
      maxUsagePerCustomer: undefined,
      startDate: '',
      endDate: '',
      priority: 0,
      stackable: false,
      applyToAllProducts: true,
      badgeColor: '#FF5722',
      internalNotes: '',
      shopRefIds: [],
      productRefIds: [],
      categoryIds: [],
    },
  });

  const formValues = form.watch();
  const promotionType = formValues.type;

  // Calculate form progress
  const { completedFields, totalFields } = useMemo(() => {
    const requiredFields = ['name', 'type', 'value', 'startDate', 'endDate'];
    const completed = requiredFields.filter((field) => {
      const value = formValues[field as keyof CreatePromotionFormData];
      if (field === 'value') return typeof value === 'number' && value >= 0;
      return value !== '' && value !== undefined;
    }).length;

    return { completedFields: completed, totalFields: requiredFields.length };
  }, [formValues]);

  const onSubmit = (data: CreatePromotionFormData) => {
    // Determine applyToAllProducts and IDs based on selection mode (same as mobile)
    const shouldApplyToAll = selectionMode === 'all';
    const productRefIds = selectionMode === 'products' ? selectedProductIds : undefined;
    const shopRefIds = selectionMode === 'shops' ? selectedShopIds : undefined;

    // Calculate multi-currency values for fixed_amount and bundle types
    let valueKmf: number | undefined;
    let valueUsd: number | undefined;
    let valueEur: number | undefined;

    if ((data.type === 'fixed_amount' || data.type === 'bundle') && data.value > 0) {
      // Convert value from selected currency to all currencies
      valueKmf = Math.round(convertPrice(data.value, valueCurrency, 'comoros_currency', exchangeRates));
      valueUsd = Math.round(convertPrice(data.value, valueCurrency, 'american_currency', exchangeRates) * 100) / 100;
      valueEur = Math.round(convertPrice(data.value, valueCurrency, 'european_currency', exchangeRates) * 100) / 100;
    }

    const cleanData = {
      ...data,
      displayName: data.displayName || undefined,
      description: data.description || undefined,
      buyQuantity: data.type === 'buy_x_get_y' ? data.buyQuantity : undefined,
      getQuantity: data.type === 'buy_x_get_y' ? data.getQuantity : undefined,
      minPurchaseAmount: data.minPurchaseAmount || undefined,
      minQuantity: data.minQuantity || undefined,
      maxUsage: data.maxUsage || undefined,
      maxUsagePerCustomer: data.maxUsagePerCustomer || undefined,
      priority: data.priority || undefined,
      internalNotes: data.internalNotes || undefined,
      applyToAllProducts: shouldApplyToAll,
      shopRefIds,
      productRefIds,
      categoryIds: undefined,
      // Multi-currency values
      valueKmf,
      valueUsd,
      valueEur,
    };

    createPromotion(cleanData, {
      onSuccess: () => {
        toast.success(t('promotions.createSuccess', 'Promotion créée avec succès'));
        navigate('/promotions');
      },
      onError: (error) => {
        console.error('Failed to create promotion:', error);
        toast.error(t('promotions.createError', 'Échec de la création de la promotion'));
      },
    });
  };

  // Toggle product selection
  const toggleProduct = (refId: string) => {
    setSelectedProductIds((prev) =>
      prev.includes(refId) ? prev.filter((id) => id !== refId) : [...prev, refId]
    );
  };

  // Toggle shop selection
  const toggleShop = (refId: string) => {
    setSelectedShopIds((prev) =>
      prev.includes(refId) ? prev.filter((id) => id !== refId) : [...prev, refId]
    );
  };

  return (
    <div className="pb-6">
      <FormPageHeader
        backLink="/promotions"
        title={t('promotions.addPromotion', 'Nouvelle promotion')}
        description={t('promotions.addPromotionDescription', 'Créer une nouvelle offre promotionnelle')}
      />

      <FormProgressIndicator
        completed={completedFields}
        total={totalFields}
        label={t('common.fieldsCompleted', 'champs complétés')}
      />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-6 lg:grid lg:grid-cols-3">
            {/* Sidebar */}
            <div className="order-first lg:order-last space-y-6">
              {/* Appearance Card */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('promotions.appearance', 'Apparence')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="badgeColor"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('promotions.badgeColor', 'Couleur du badge')}</FormLabel>
                        <div className="grid grid-cols-8 gap-2">
                          {BADGE_COLORS.map((color) => (
                            <button
                              key={color}
                              type="button"
                              onClick={() => field.onChange(color)}
                              className={`size-8 rounded-full border-2 flex items-center justify-center transition-all ${
                                field.value === color
                                  ? 'border-primary scale-110'
                                  : 'border-transparent hover:scale-105'
                              }`}
                              style={{ backgroundColor: color }}
                            >
                              {field.value === color && (
                                <Check className="size-4 text-white" />
                              )}
                            </button>
                          ))}
                        </div>
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Activation Card */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('promotions.activation', 'Activation')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <p className="text-sm font-medium">{t('promotions.activateNow', 'Activer maintenant')}</p>
                      <p className="text-xs text-muted-foreground">
                        {t('promotions.activateNowDesc', 'La promotion sera active immédiatement')}
                      </p>
                    </div>
                    <Switch
                      checked={activateNow}
                      onCheckedChange={setActivateNow}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Submit button - desktop */}
              <div className="hidden lg:block sticky bottom-4">
                <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="size-4 mr-2 animate-spin" />}
                  {t('promotions.createPromotion', 'Créer la promotion')}
                </Button>
              </div>
            </div>

            {/* Main form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Information */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('promotions.basicInfo', 'Informations de base')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('promotions.name', 'Nom')} *</FormLabel>
                        <FormControl>
                          <Input placeholder={t('promotions.namePlaceholder', 'ex: Soldes été 2024')} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="displayName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('promotions.displayName', 'Nom affiché')}</FormLabel>
                        <FormControl>
                          <Input placeholder={t('promotions.displayNamePlaceholder', 'Nom visible par les clients')} {...field} />
                        </FormControl>
                        <FormDescription>
                          {t('promotions.displayNameDesc', 'Si vide, le nom sera utilisé.')}
                        </FormDescription>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('promotions.description', 'Description')}</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder={t('promotions.descriptionPlaceholder', 'Décrivez la promotion...')}
                            className="resize-none"
                            rows={3}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Promotion Type */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('promotions.discountInfo', 'Réduction')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('promotions.type', 'Type')} *</FormLabel>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {PROMOTION_TYPES.map((type) => {
                            const TypeIcon = typeIconMap[type as PromotionType] || Tag;
                            const isSelected = field.value === type;
                            return (
                              <button
                                key={type}
                                type="button"
                                onClick={() => field.onChange(type)}
                                className={`flex items-center gap-2 p-3 rounded-lg border transition-colors ${
                                  isSelected
                                    ? 'border-primary bg-primary/10 text-primary'
                                    : 'border-input hover:border-primary/50'
                                }`}
                              >
                                <TypeIcon className="size-4" />
                                <span className="text-sm font-medium">
                                  {t(`promotions.types.${type}`, type)}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Value field */}
                  {promotionType !== 'free_shipping' && (
                    <>
                      {promotionType === 'percentage' ? (
                        <FormField
                          control={form.control}
                          name="value"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('promotions.percentageValue', 'Pourcentage')} *</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Input
                                    type="number"
                                    min={0}
                                    max={100}
                                    step={1}
                                    placeholder="0"
                                    {...field}
                                    value={field.value ?? ''}
                                    onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : 0)}
                                  />
                                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      ) : (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <FormField
                              control={form.control}
                              name="value"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>{t('promotions.amount', 'Montant')} *</FormLabel>
                                  <FormControl>
                                    <Input
                                      type="number"
                                      min={0}
                                      step={0.01}
                                      placeholder="0.00"
                                      {...field}
                                      value={field.value ?? ''}
                                      onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : 0)}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormItem>
                              <FormLabel>{t('promotions.currency', 'Devise')} *</FormLabel>
                              <Select
                                value={valueCurrency}
                                onValueChange={(value) => setValueCurrency(value as CurrencyType)}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {CURRENCIES.map((currency) => (
                                    <SelectItem key={currency.value} value={currency.value}>
                                      {currency.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </FormItem>
                          </div>

                          {/* Price equivalents for fixed_amount and bundle */}
                          {formValues.value > 0 && (
                            <PriceConverter
                              price={formValues.value}
                              sourceCurrency={valueCurrency}
                              compact
                            />
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* Buy X Get Y fields */}
                  {promotionType === 'buy_x_get_y' && (
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="buyQuantity"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('promotions.buyQuantity', 'Quantité à acheter')} *</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                placeholder="2"
                                {...field}
                                value={field.value ?? ''}
                                onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="getQuantity"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('promotions.getQuantity', 'Quantité gratuite')} *</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                placeholder="1"
                                {...field}
                                value={field.value ?? ''}
                                onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Product Selection - Same as mobile-pro */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('promotions.productSelection', 'Sélection des produits')}</CardTitle>
                  <CardDescription>
                    {t('promotions.productSelectionDesc', 'Choisissez les produits concernés par cette promotion')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Selection Mode - Mutually exclusive like mobile */}
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setSelectionMode('all')}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                        selectionMode === 'all'
                          ? 'border-primary bg-primary/10'
                          : 'border-input hover:border-primary/50'
                      }`}
                    >
                      <div className={`size-5 rounded-full border-2 flex items-center justify-center ${
                        selectionMode === 'all' ? 'border-primary bg-primary' : 'border-muted-foreground'
                      }`}>
                        {selectionMode === 'all' && <Check className="size-3 text-white" />}
                      </div>
                      <div className="text-left">
                        <p className="font-medium">{t('promotions.applyToAllProducts', 'Tous les produits')}</p>
                        <p className="text-xs text-muted-foreground">
                          {t('promotions.applyToAllProductsDesc', 'La promotion s\'applique à tous vos produits')}
                        </p>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setSelectionMode('shops')}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                        selectionMode === 'shops'
                          ? 'border-primary bg-primary/10'
                          : 'border-input hover:border-primary/50'
                      }`}
                    >
                      <div className={`size-5 rounded-full border-2 flex items-center justify-center ${
                        selectionMode === 'shops' ? 'border-primary bg-primary' : 'border-muted-foreground'
                      }`}>
                        {selectionMode === 'shops' && <Check className="size-3 text-white" />}
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-medium">{t('promotions.selectShops', 'Boutiques spécifiques')}</p>
                        <p className="text-xs text-muted-foreground">
                          {t('promotions.selectShopsDesc', 'Choisir des boutiques spécifiques')}
                        </p>
                      </div>
                      {selectionMode === 'shops' && selectedShopIds.length > 0 && (
                        <Badge variant="secondary">{selectedShopIds.length}</Badge>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => setSelectionMode('products')}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                        selectionMode === 'products'
                          ? 'border-primary bg-primary/10'
                          : 'border-input hover:border-primary/50'
                      }`}
                    >
                      <div className={`size-5 rounded-full border-2 flex items-center justify-center ${
                        selectionMode === 'products' ? 'border-primary bg-primary' : 'border-muted-foreground'
                      }`}>
                        {selectionMode === 'products' && <Check className="size-3 text-white" />}
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-medium">{t('promotions.selectProducts', 'Produits spécifiques')}</p>
                        <p className="text-xs text-muted-foreground">
                          {t('promotions.selectProductsDesc', 'Choisir des produits spécifiques')}
                        </p>
                      </div>
                      {selectionMode === 'products' && selectedProductIds.length > 0 && (
                        <Badge variant="secondary">{selectedProductIds.length}</Badge>
                      )}
                    </button>
                  </div>

                  {/* Shop selector button */}
                  {selectionMode === 'shops' && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => setShowShopSelector(true)}
                    >
                      <Store className="size-4 mr-2" />
                      {selectedShopIds.length > 0
                        ? t('promotions.shopsSelected', '{{count}} boutique(s) sélectionnée(s)', { count: selectedShopIds.length })
                        : t('promotions.selectShopsBtn', 'Sélectionner des boutiques')}
                    </Button>
                  )}

                  {/* Product selector button */}
                  {selectionMode === 'products' && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => setShowProductSelector(true)}
                    >
                      <Package className="size-4 mr-2" />
                      {selectedProductIds.length > 0
                        ? t('promotions.productsSelected', '{{count}} produit(s) sélectionné(s)', { count: selectedProductIds.length })
                        : t('promotions.selectProductsBtn', 'Sélectionner des produits')}
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Period */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('promotions.period', 'Période')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="startDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('promotions.startDate', 'Date de début')} *</FormLabel>
                          <FormControl>
                            <Input type="datetime-local" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="endDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('promotions.endDate', 'Date de fin')} *</FormLabel>
                          <FormControl>
                            <Input type="datetime-local" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Limits Section */}
              <Card>
                <CardHeader
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => setLimitsOpen(!limitsOpen)}
                >
                  <div className="flex items-center justify-between">
                    <CardTitle>{t('promotions.requirements', 'Limites & Conditions')}</CardTitle>
                    {limitsOpen ? <ChevronUp className="size-5" /> : <ChevronDown className="size-5" />}
                  </div>
                </CardHeader>
                {limitsOpen && (
                  <CardContent className="space-y-4 pt-0">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="minPurchaseAmount"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('promotions.minPurchase', 'Montant min. d\'achat')}</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  min={0}
                                  step={0.01}
                                  placeholder="0.00"
                                  {...field}
                                  value={field.value ?? ''}
                                  onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="minQuantity"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('promotions.minQuantity', 'Quantité min.')}</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  placeholder="1"
                                  {...field}
                                  value={field.value ?? ''}
                                  onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="maxUsage"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('promotions.maxUsage', 'Utilisations max.')}</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  placeholder={t('promotions.unlimited', 'Illimité')}
                                  {...field}
                                  value={field.value ?? ''}
                                  onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="maxUsagePerCustomer"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('promotions.maxPerCustomer', 'Max. par client')}</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  placeholder={t('promotions.unlimited', 'Illimité')}
                                  {...field}
                                  value={field.value ?? ''}
                                  onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </CardContent>
                )}
              </Card>

              {/* Settings Section */}
              <Card>
                <CardHeader
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => setSettingsOpen(!settingsOpen)}
                >
                  <div className="flex items-center justify-between">
                    <CardTitle>{t('promotions.settings', 'Paramètres')}</CardTitle>
                    {settingsOpen ? <ChevronUp className="size-5" /> : <ChevronDown className="size-5" />}
                  </div>
                </CardHeader>
                {settingsOpen && (
                  <CardContent className="space-y-4 pt-0">
                      <FormField
                        control={form.control}
                        name="stackable"
                        render={({ field }) => (
                          <FormItem className="flex items-center justify-between rounded-lg border p-3">
                            <div className="space-y-0.5">
                              <FormLabel>{t('promotions.stackable', 'Cumulable')}</FormLabel>
                              <FormDescription className="text-xs">
                                {t('promotions.stackableDesc', 'Peut être combinée avec d\'autres promotions')}
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="priority"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('promotions.priority', 'Priorité')}</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min={0}
                                placeholder="0"
                                {...field}
                                value={field.value ?? ''}
                                onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                              />
                            </FormControl>
                            <FormDescription className="text-xs">
                              {t('promotions.priorityDesc', 'Les promotions avec une priorité plus élevée sont appliquées en premier')}
                            </FormDescription>
                          </FormItem>
                        )}
                      />
                    </CardContent>
                )}
              </Card>

              {/* Internal Notes Section */}
              <Card>
                <CardHeader
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => setNotesOpen(!notesOpen)}
                >
                  <div className="flex items-center justify-between">
                    <CardTitle>{t('promotions.internalNotes', 'Notes internes')}</CardTitle>
                    {notesOpen ? <ChevronUp className="size-5" /> : <ChevronDown className="size-5" />}
                  </div>
                </CardHeader>
                {notesOpen && (
                  <CardContent className="pt-0">
                      <FormField
                        control={form.control}
                        name="internalNotes"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Textarea
                                placeholder={t('promotions.internalNotesPlaceholder', 'Notes visibles uniquement par votre équipe...')}
                                className="resize-none"
                                rows={4}
                                {...field}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </CardContent>
                )}
              </Card>

              {/* Submit button - mobile */}
              <div className="lg:hidden">
                <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
                  {isSubmitting && <Loader2 className="size-4 mr-2 animate-spin" />}
                  {t('promotions.createPromotion', 'Créer la promotion')}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </Form>

      {/* Product Selector Modal */}
      <Dialog open={showProductSelector} onOpenChange={setShowProductSelector}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{t('promotions.selectProducts', 'Sélectionner des produits')}</DialogTitle>
          </DialogHeader>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder={t('promotions.searchProducts', 'Rechercher des produits...')}
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              className="pl-9 pr-9"
            />
            {productSearch && (
              <button
                type="button"
                onClick={() => setProductSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <X className="size-4 text-muted-foreground" />
              </button>
            )}
          </div>

          <div className="text-sm text-muted-foreground">
            {t('promotions.productsSelected', '{{count}} produit(s) sélectionné(s)', { count: selectedProductIds.length })}
          </div>

          <div className="flex-1 overflow-y-auto space-y-2 min-h-[300px]">
            {isLoadingProducts ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-3 p-2">
                    <Skeleton className="size-5" />
                    <Skeleton className="size-12" />
                    <div className="flex-1 space-y-1">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Package className="size-12 mb-2" />
                <p>{t('promotions.noProductsFound', 'Aucun produit trouvé')}</p>
              </div>
            ) : (
              products.map((product) => {
                const productId = product.refId || product.id;
                const isSelected = selectedProductIds.includes(productId);
                const imageUrl = getImageUrl(product.images?.[0]) || '';
                return (
                  <button
                    key={productId}
                    type="button"
                    onClick={() => toggleProduct(productId)}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg border transition-colors ${
                      isSelected ? 'border-primary bg-primary/10' : 'border-transparent hover:bg-muted'
                    }`}
                  >
                    <Checkbox checked={isSelected} />
                    {imageUrl ? (
                      <img src={imageUrl} alt={product.name} className="size-12 rounded object-cover" />
                    ) : (
                      <div className="size-12 rounded bg-muted flex items-center justify-center">
                        <Package className="size-6 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 text-left">
                      <p className="font-medium line-clamp-1">{product.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {product.prices?.euro ? formatPrice(product.prices.euro, 'EUR') : '-'}
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProductSelector(false)}>
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button onClick={() => setShowProductSelector(false)}>
              {t('common.confirm', 'Confirmer')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shop Selector Modal */}
      <Dialog open={showShopSelector} onOpenChange={setShowShopSelector}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{t('promotions.selectShops', 'Sélectionner des boutiques')}</DialogTitle>
          </DialogHeader>

          <div className="text-sm text-muted-foreground">
            {t('promotions.shopsSelected', '{{count}} boutique(s) sélectionnée(s)', { count: selectedShopIds.length })}
          </div>

          <div className="flex-1 overflow-y-auto space-y-2 min-h-[200px]">
            {shops.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Store className="size-12 mb-2" />
                <p>{t('promotions.noShopsFound', 'Aucune boutique trouvée')}</p>
              </div>
            ) : (
              shops.map((shop) => {
                const isSelected = selectedShopIds.includes(shop.refId || shop.id);
                const imageUrl = getImageUrl(shop.images?.[0]) || shop.logo;
                return (
                  <button
                    key={shop.refId || shop.id}
                    type="button"
                    onClick={() => toggleShop(shop.refId || shop.id)}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg border transition-colors ${
                      isSelected ? 'border-primary bg-primary/10' : 'border-transparent hover:bg-muted'
                    }`}
                  >
                    <Checkbox checked={isSelected} />
                    {imageUrl ? (
                      <img src={imageUrl} alt={shop.name} className="size-10 rounded object-cover" />
                    ) : (
                      <div className="size-10 rounded bg-muted flex items-center justify-center">
                        <Store className="size-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 text-left">
                      <p className="font-medium">{shop.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {[shop.city, shop.country].filter(Boolean).join(', ') || '-'}
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShopSelector(false)}>
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button onClick={() => setShowShopSelector(false)}>
              {t('common.confirm', 'Confirmer')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
